/*
 * (c) Copyright, 2023, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import React, { useContext, useEffect, useState } from 'react'
import { Button, Form, FormGroup } from 'react-bootstrap'
import { Field, Formik } from 'formik'
import { toast } from '~/services/toast'
import { useTranslation } from 'react-i18next'
import { addFileToFavorite, addFileToFavoriteLegacy, getDetails } from '~/services/api/files'
import _ from 'lodash'
import Validation from '~/lib/Validation'
import Modal from '~/components/Modal'
import { ErrorMessageStyled } from '~/components/ErrorMessage'
import { File } from '~/constants/file'
import { useServiceContext } from '~/root'

import type { FC } from 'react'
import type { FormikProps } from 'formik'
import type { FileDetail } from '~/services/api/files'
import { useEventBus } from '~/hooks/useEventbus'

interface FavoriteNameModalProps {
  fileDetails: any
  onClose?: () => void
}

interface FavoriteNameFormValues {
  favoriteName: string
  ext?: string
}

const FolderAndDocumentFavoriteNameModal: FC<FavoriteNameModalProps> = ({ fileDetails, onClose }) => {
  const context = useServiceContext()
  const { emit } = useEventBus()

  const { $rootScope } = context
  const { t: translate } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [file, setFile] = useState<FileDetail & { ext?: string; favoriteName?: string }>({ ...fileDetails })

  useEffect(() => {
    setIsOpen(true)
    loadData()
  }, [fileDetails])

  const doClose = () => {
    setIsOpen(false)
    if (onClose) {
      onClose()
    }
  }

  const loadData = (): void => {
    if (!fileDetails?.id) {
      const errMsg = 'Incorrect fsi object'
      toast.error(errMsg)
      throw new Error(errMsg)
    }

    if (!fileDetails?.type) {
      // if there is no TYPE in the fsi object,
      // download fresh data from server to ensure it's not an invalid fsi
      getDetails(fileDetails.id).then(processFavoriteName, function () {
        doClose()
      })
    } else {
      processFavoriteName(fileDetails)
    }
  }

  const processFavoriteName = (data: FileDetail & { ext?: string; favoriteName?: string }): void => {
    const updated: FileDetail & { ext?: string; favoriteName?: string } = { ...data }
    if (
      _.includes([File.type.DOCUMENT, File.type.REFERENCE], updated.type) &&
      typeof updated.favoriteName === 'string' &&
      updated.favoriteName.indexOf('.') !== -1
    ) {
      const parts = updated.favoriteName.split('.')
      updated.ext = '.' + parts.pop()
      updated.favoriteName = parts.join('.')
    }
    setFile(updated)
  }

  const save = (values: FavoriteNameFormValues): Promise<any> => {
    const apiCall = addFileToFavoriteLegacy(fileDetails?.id ?? '', (values.favoriteName ?? '') + (values.ext ?? ''))

    apiCall.then(
      function () {
        // Emit global events for backward compatibility (window event bus)
        window.dispatchEvent(new CustomEvent('bctable:updateTable', { detail: true }))
        window.dispatchEvent(new CustomEvent('updateFiles'))
        window.dispatchEvent(new CustomEvent('updateDashboard'))
        window.dispatchEvent(new CustomEvent('updateSearchFiles'))
        window.dispatchEvent(new CustomEvent('updateFavorites'))
        emit('updateFavorites')
        toast.success(translate('ADD_FAV_SUCCESS', { filename: file?.name ?? '' }))
        fileDetails.isFavorite = true
      },
      function (data) {
        if (data?.data?.code === 417) {
          toast.error(translate('UNDERLYINF_DATA_WERE_CHNGD_TRY_AGAIN'))
        } else {
          toast.error(translate('ERROR_ERROR', { error: data?.data?.message }))
        }
      }
    )

    return apiCall
  }

  return (
    <>
      <Formik
        validate={(values: FavoriteNameFormValues) => {
          const errors: { favoriteName?: string } = {}
          if (values?.favoriteName === '') errors.favoriteName = Validation.general.mixed.required
          return errors
        }}
        enableReinitialize={true}
        innerRef={(el: FormikProps<FavoriteNameFormValues> | null) => {
          if (context.store) {
            ;(context.store as any).formRef = el
          }
        }}
        initialValues={{
          favoriteName: file.favoriteName || '',
          ext: file.ext || ''
        }}
        onSubmit={(values: FavoriteNameFormValues) => {
          save(values)
          doClose()
        }}
      >
        {({ values, handleSubmit, setFieldValue }: FormikProps<FavoriteNameFormValues>) => (
          <div>
            <Modal
              className="theme-sandman"
              title={translate('BROWSER_ACTION_ADD_TO_FAV')}
              show={isOpen}
              handleClose={doClose}
            >
              <Modal.Body>
                <Form.Label className="mb0">
                  {file?.type === File.type.FOLDER ? translate('FOLDER_NAME') : translate('DOCUMENT_NAME')}
                </Form.Label>
                <p className="whitespaceprewrap fs12">{file?.name ?? ''}</p>
                <div className="form-group md-form-group">
                  <label
                    htmlFor="0803f546-82b9-4529-a6a8-f54868d80274"
                    data-testid="favorited-name-label"
                    style={{ fontWeight: 600, display: 'block', marginBottom: 4, fontSize: '12px' }}
                  >
                    {translate('FSI_FAVORITE_NAME_LABEL')}
                  </label>
                  <input
                    type="text"
                    name="favoriteName"
                    className="md-input"
                    maxLength={234}
                    id="0803f546-82b9-4529-a6a8-f54868d80274"
                    value={values.favoriteName}
                    onChange={(e) => setFieldValue('favoriteName', e.target.value)}
                    autoFocus
                  />
                  <ErrorMessageStyled name="favoriteName" classesName="fs12" />
                  {values.ext && <span className="ext-txt">{values.ext}</span>}
                </div>
              </Modal.Body>
              <Modal.Footer>
                <Button data-action={'ok'} variant="link" onClick={doClose} id="888d5189-f234-407a-a603-9260da169e91">
                  {translate('CANCEL')}
                </Button>
                <Button
                  data-action={'ok'}
                  className="btn btn-primary "
                  onClick={handleSubmit as any}
                  id="7b19e79a-2a98-49de-96b0-b58029331741"
                >
                  {translate('SAVE_BTN')}
                </Button>
              </Modal.Footer>
            </Modal>
          </div>
        )}
      </Formik>
    </>
  )
}

export default FolderAndDocumentFavoriteNameModal
